import { trackEvent } from "src/Amplitude";
import {
  Paper,
  Box,
  Typography,
  Stack,
  TableContainer,
  Table,
  useTheme,
  TableRow,
  TableCell,
  TableBody,
  Collapse,
  Button,
  Pagination,
  Link,
} from "@mui/material";

import React from "react";
import { PaginationParams } from "src/components/markets/Table/MarketTable";
import TargetCancelReqIcon from "src/icons/TargetCancelReqIcon";
import TargetIOrderIcon from "src/icons/TargetIOrderIcon";
import TargetOrderExecuteIcon from "src/icons/TargetOrderExecuteIcon";
import { TargetOrderCtRes, useGetTargetOrderCtQuery } from "src/store/features/api/generated";
import { useTargetState } from "src/store/features/TargetOrders.slice";
import CountryOrCryptoFlag from "src/components/common/CountryOrCryptoFlag";
import { formatCurrency } from "src/utils/helpers/PriceAndUnitsHelpers";
import { timestampFormatter } from "src/utils/helpers/DateAndTimeHelpers";
import TargetOrdersFilters from "../form-control/TargetOrdersFilters";
import TargetOrderCancel from "../target-modals/TargetOrderCancel";
import TargetOrderExecute from "../target-modals/TargetOrderExecute";
import usePermission from "src/utils/hooks/usePermission";

interface RowProps {
  row: TargetOrderCtRes;
  index: number;
  executeData: React.Dispatch<React.SetStateAction<any>>;
  cancelData: React.Dispatch<React.SetStateAction<any>>;
  cancelModal: React.Dispatch<React.SetStateAction<boolean>>;
  executeModal: React.Dispatch<React.SetStateAction<boolean>>;
}
function Row(props: RowProps) {
  const { row, cancelModal, cancelData, index } = props;
  const [open, setOpen] = React.useState(false);
  let customerDate = new Date().toLocaleString();
  const valueOne = row.lines.find(
    (x: any) => x.side === (row.targetType === "buy" ? "Buy" : "Sell"),
  );

  const targetPrice = row.lines.find((item: any) => item.known === true)?.price;
  const withinFivePercentage: boolean =
    row.percentageDifference > -5 && row.percentageDifference < 5;
  const gamOrCanceled = row.gamRequested || row.cancelRequested;
  // Cts enter pending state while netsuite creates the CT
  const ctPending = row.status === "Target Order Requested";
  return (
    <React.Fragment>
      <TableRow
        key={index}
        sx={{
          "& > *": { borderBottom: "unset" },
        }}
        onClick={() => {
          setOpen(!open);
          trackEvent("Target_Order_Expand_Row_Mobile");
        }}>
        <TableCell align="center" sx={{ borderBottom: "none" }}>
          {row.cancelRequested && <TargetCancelReqIcon />}
          {row.gamRequested && <TargetOrderExecuteIcon />}
          {!row.gamRequested && !row.cancelRequested && (
            <Typography> {(row.percentageDifference ?? 0).toFixed(2) + `%`}</Typography>
          )}
        </TableCell>
        <TableCell sx={{ borderBottom: "none" }}>
          <Stack direction="row" sx={{ position: "relative " }}>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                borderBottom: "none",
                marginRight: "2px",
              }}>
              {row.lines[0].currencyConfig.code}/{row.lines[1].currencyConfig.code}
            </Typography>
            {withinFivePercentage && (
              <Box
                sx={{
                  position: "absolute",
                  top: "-8px",
                  left: "53px",
                }}>
                <TargetIOrderIcon />
              </Box>
            )}
          </Stack>
          <Typography sx={{ fontSize: "14px" }}>{timestampFormatter(row.created)}</Typography>
          <Typography sx={{ textTransform: "capitalize", fontSize: "14px" }}>
            {row.targetType} Order
          </Typography>
        </TableCell>
        <TableCell sx={{ borderBottom: "none" }}>
          <Box>
            <Stack direction="row" justifyContent="flex-end" alignItems="center">
              <Typography sx={{ marginRight: "8px", fontSize: "14px" }}>
                {row.lines[1].units}
              </Typography>
              <CountryOrCryptoFlag
                name={valueOne?.currencyConfig.logoUrl || valueOne?.currencyConfig.code}
                size={17}
              />
            </Stack>
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          style={{
            paddingBottom: 0,

            paddingTop: 0,
          }}
          colSpan={4}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                display: "flex",
                marginTop: "5px",
                width: "78%",
                marginLeft: "auto",
                justifyContent: "space-between",
                marginRight: 0,
              }}>
              <Typography sx={{ fontSize: "14px" }}>Target Price</Typography>
              <Typography sx={{ fontSize: "14px" }}>
                {formatCurrency(targetPrice || 0, row.quoteCurrencyConfig.code)}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                marginTop: "5px",
                fontSize: "14px",
                width: "78%",
                marginLeft: "auto",
                justifyContent: "space-between",
              }}>
              <Typography sx={{ fontSize: "14px" }}>Current Price</Typography>
              <Typography sx={{ fontSize: "14px" }}>
                {formatCurrency(row.currentPrice || 0, valueOne?.quoteCurrencyConfig.code || "USD")}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                width: "78%",
                marginLeft: "auto",
                justifyContent: "space-between",
              }}>
              {row.cancelRequested && (
                <TableCell
                  sx={{ borderBottom: "none", paddingLeft: "0px" }}
                  align="left"
                  colSpan={2}>
                  <Typography
                    sx={{
                      borderBottom: "none",
                      fontWeight: 500,
                      fontSize: "14px",
                      textAlign: "center",
                      textTransform: "capitalize",
                    }}
                    align="center">
                    Requested to Cancel
                  </Typography>
                </TableCell>
              )}
              {ctPending && !gamOrCanceled && (
                <TableCell
                  sx={{ borderBottom: "none", paddingLeft: "0px" }}
                  align="left"
                  colSpan={2}>
                  <Typography
                    sx={{
                      borderBottom: "none",
                      fontWeight: 500,
                      fontSize: "14px",
                      textTransform: "capitalize",
                    }}
                    align="center">
                    Pending
                  </Typography>
                </TableCell>
              )}
            </Box>
            <Stack direction="row" spacing={1} sx={{ width: "100%", marginBottom: "10px" }}>
              {!gamOrCanceled && !ctPending && (
                <>
                  <Button
                    disableRipple
                    sx={{
                      height: "25px",
                      borderRadius: "20px",
                      textTransform: "capitalize",
                    }}
                    fullWidth
                    onClick={(e) => {
                      e.stopPropagation();
                      trackEvent("Target_Order_Mobile_Cancel");
                      cancelData({
                        ctId: row.id,
                        datetime: customerDate,
                        type: row.targetType,
                        boxShadow: "none",
                        tradeUnits: valueOne?.units,
                        currentPrice: row.currentPrice,
                        targetPrice: targetPrice,
                        currencyConvertCode: row.quoteCurrencyConfig.code,
                        currencyTradeCode: valueOne?.currencyConfig.code,
                      });
                      cancelModal(true);
                    }}
                    variant="outlined">
                    Cancel
                  </Button>
                </>
              )}
            </Stack>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

const TargetOrderDataTableMobile = () => {
  const [executeModal, setExecuteModal] = React.useState(false);
  const [executeData, setExecuteData] = React.useState(false);
  const [cancelModal, setCancelModal] = React.useState(false);
  const [cancelData, setCancelData] = React.useState({
    ctId: "",
    datetime: "",
  });
  const [paginationParams, setPaginationParams] = React.useState<PaginationParams>({
    limit: 10,
    page: 1,
  });
  const { filterOption, sortOption, ctSuccess } = useTargetState();
  const { data, isLoading, refetch } = useGetTargetOrderCtQuery({
    limit: 6,
    page: paginationParams.page,
    sortType: sortOption,
    filterType: filterOption,
  });

  const theme = useTheme();
  const { isGuest } = usePermission();

  const handlePagination = (event: unknown, newPage: number): void => {
    setPaginationParams((prev: PaginationParams): PaginationParams => ({ ...prev, page: newPage }));
  };

  React.useEffect(() => {
    // refetch on change.
    [ctSuccess, sortOption, filterOption].some((x) => x) && refetch();
  }, [ctSuccess, sortOption, filterOption]);

  return (
    <Paper
      sx={{
        minWidth: "100%",
        height: "100%",
        borderRadius: "20px",
        boxShadow: " 0px 4px 20px rgba(173, 192, 203, 0.3)",
      }}>
      {!isGuest && (
        <>
          <TargetOrderCancel
            cancelReq={cancelData}
            refetchData={refetch}
            open={cancelModal}
            handleClose={setCancelModal}
          />
          <TargetOrderExecute
            open={executeModal}
            refetchData={refetch}
            executeData={executeData}
            handleClose={setExecuteModal}
          />
        </>
      )}

      <Box
        sx={{
          height: "28px",
          width: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "48px",
          padding: "50px 10px 0px 10px",
        }}>
        <Typography sx={{ fontWeight: 600, fontSize: "14px", paddingLeft: "5%" }}>
          Targets
        </Typography>
        <Stack direction="row" sx={{ alignItems: "center", justifyContent: "flex-end" }}>
          <TargetOrdersFilters disabled={Boolean(!data?.data.length)} />
        </Stack>
      </Box>

      <TableContainer>
        <Table aria-label="collapsible table">
          <TableBody id="target-order-tablebody">
            {data &&
              data.data.map((row, ind) => (
                <Row
                  key={ind}
                  row={row}
                  index={ind}
                  cancelModal={setCancelModal}
                  executeModal={setExecuteModal}
                  executeData={setExecuteData}
                  cancelData={setCancelData}
                />
              ))}

            {!data?.data.length && !isLoading && (
              <>
                <Box
                  sx={{
                    height: "408px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}>
                  <Typography sx={{ textAlign: "center", padding: "0px 20px" }}>
                    You currently have no open target open with us. Place a target on the left and
                    track your order here
                  </Typography>
                </Box>
                <Box
                  sx={{
                    height: "150px",
                    backgroundColor: "#f8f9f9",
                    display: "flex",
                    flexDirection: "column",
                    marginLeft: "16px",
                    marginRight: "16px",
                    justifyContent: "center",
                    alignItems: "center",
                  }}>
                  <Stack sx={{ textAlign: "center" }}>
                    <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>
                      Not sure what a target is or how to place one?
                    </Typography>
                    <Typography sx={{ fontSize: "14px" }}>
                      Speak to your broker here or view our quick links below
                    </Typography>
                  </Stack>
                  <Stack
                    sx={{
                      marginTop: "20px",
                      textAlign: "center",
                      color: theme.palette.secondary.main,
                    }}>
                    <Typography sx={{ fontSize: "14px" }}>
                      <Link
                        href="https://support.calebandbrown.com/en/articles/11182116-how-to-place-a-target-order-limit-order"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: theme.palette.secondary.main }}>
                        How to place a target order?
                      </Link>
                    </Typography>
                  </Stack>
                </Box>
              </>
            )}
            {isLoading && <Typography>Loading</Typography>}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 2,
          pb: 4,
        }}>
        {data && (
          <Pagination
            size="small"
            count={data?.totalPages}
            page={data?.currentPage}
            onChange={handlePagination}
            showFirstButton
            showLastButton
          />
        )}
      </Box>
    </Paper>
  );
};

export default TargetOrderDataTableMobile;
